import { morphoTypeMarkets } from '@1delta/data-sdk'
import { formatEther, zeroAddress } from 'viem'
import { decodeMarkets, ListaMarket } from '../morpho/utils/evmParser'
import { AdditionalYields } from '../../../types'
import { GenericTokenList } from '../../../types'
import type { FixedTermInfo } from '../../../types/lenderTypes'
import {
  formatNr,
  parseLtv,
  liquidationPenaltyFromLltv,
} from '../morpho/utils/parsers'
import { MorphoGeneralPublicResponse } from '../../../types/lender/morpho-types'
import { toGenericPriceKey, toOracleKey } from '../../../assets'
import { MathLib } from '../morpho/utils/mathLib'
import { apyToApr } from '../../user-data/utils'
import { formatMorphoPrice } from '../../../utils/parsing'
import { createMarketUid } from '../../../utils'
import { LISTA_MARKET_CHUNK_SIZE } from './publicCallBuild'
import { getCachedListaFixedTerms, fetchListaFixedTerms } from './listaBroker'

export function getListaMarketDataConverter(
  lender: string,
  chainId: string,
  prices: any,
  additionalYields: AdditionalYields = {
    intrinsicYields: {},
    lenderRewards: {},
    loaded: true,
  },
  tokens: GenericTokenList = {},
  marketsOverride?: string[],
): [(data: any[]) => any | undefined, number] {
  const markets =
    marketsOverride ?? morphoTypeMarkets()?.LISTA_DAO?.[chainId] ?? []
  // Fire-and-forget warm of the fixed-term cache for callers that reach this
  // converter without priming it. It can only serve the NEXT call — the
  // converter below is synchronous and reads the cache as it is now — so every
  // real entry point awaits `fetchListaFixedTerms` itself before converting
  // (`fetchListaPublicData` here, `getLenderPublicData` for the cron path).
  // Single-flighted inside, so when a caller has already primed this is free.
  if (markets.length > 0)
    void fetchListaFixedTerms(chainId, markets).catch(() => {})
  return [
    (rData: any[]) => {
      // Decode all chunks and concatenate
      let decoded: ListaMarket[] = []
      for (let j = 0; j < rData.length; j++) {
        const chunkDecoded = decodeMarkets(rData[j] ?? '0x') as ListaMarket[]
        decoded.push(...chunkDecoded)
      }
      const data: { [m: string]: MorphoGeneralPublicResponse } = {}
      decoded.forEach((market, i) => {
        const uniqueKey = markets[i]
        const {
          lltv,
          irm,
          oracle,
          loanToken,
          collateralToken,
          minLoan,
          rateCap,
          rateFloor,
          broker,
          loanProvider,
          collateralProvider,
          hasWhitelist,
          ...state
        } = market
        if (collateralToken && loanToken && oracle && oracle !== zeroAddress) {
          const m = 'LISTA_DAO_' + uniqueKey.slice(2).toUpperCase()
          // @ts-ignore
          if (!data[m]) data[m] = { data: {} }

          // Moolah reuses the Morpho-style liquidation incentive: lltv (0..1)
          // drives LIF = min(1.15, 1/(0.3*lltv+0.7)) and penalty = LIF - 1.
          // The penalty applies to the seized collateral, so both config
          // entries carry it. Full liquidation is permitted: closeFactor = 1.
          const ltv = parseLtv(lltv)
          const liquidationPenalty = liquidationPenaltyFromLltv(ltv)

          // 1. LOAN asset entry
          const loanRewards: any[] = []

          const loanTokenAddress = loanToken.toLowerCase()
          const collateralAssetAddress = collateralToken.toLowerCase()
          // get assets from list
          const loanAsset = tokens[loanTokenAddress]
          const collateralAsset = tokens[collateralAssetAddress]

          const loanKey =
            toOracleKey(loanAsset?.assetGroup!) ??
            toGenericPriceKey(loanTokenAddress, chainId)

          let loanPrice = prices[loanKey]

          const loanDecimals = loanAsset?.decimals ?? 18
          const collateralDecimals = collateralAsset?.decimals ?? 18

          // try to infer loan price from collateral price if any
          if (!loanPrice) {
            const collateralKey =
              toOracleKey(collateralAsset?.assetGroup!) ??
              toGenericPriceKey(collateralAssetAddress, chainId)
            const collateralPrice = prices[collateralKey]
            // get debt price by collateral price
            if (collateralPrice) {
              const priceDebtToCollateral = formatMorphoPrice(
                state.price.toString(),
                loanDecimals,
                collateralDecimals,
              )
              loanPrice = collateralPrice / priceDebtToCollateral
            }
          }

          const meta = tokens[loanTokenAddress] ?? loanAsset
          const totalDeposits = formatNr(state.totalSupplyAssets, loanDecimals)
          const totalDebt = formatNr(state.totalBorrowAssets, loanDecimals)

          const utilization = MathLib.getUtilization({
            totalBorrowAssets: state.totalBorrowAssets,
            totalSupplyAssets: state.totalSupplyAssets,
          })

          let borrowApy = 0n
          let supplyApy = 0n

          try {
            borrowApy = MathLib.getListaBorrowApy(
              state.rateAtTarget,
              utilization,
              undefined,
              Number(state.lastUpdate),
              rateCap,
              rateFloor,
            )
            supplyApy = MathLib.getSupplyApy(borrowApy, utilization, state.fee)
          } catch {}
          const borrowApr = apyToApr(Number(formatEther(borrowApy))) * 100
          const depositApr = apyToApr(Number(formatEther(supplyApy))) * 100

          // Fixed-term (broker) rate: surface the best available fixed APR via the existing
          // stableBorrowRate column so it ingests into the DB without a schema change. The full
          // per-term menu stays on params.market.fixedTerms. Rates are in percent, like borrowApr.
          const fixedTermsForMarket =
            broker && broker !== zeroAddress
              ? getCachedListaFixedTerms(chainId, uniqueKey)
              : undefined
          const hasFixedTerms = (fixedTermsForMarket?.length ?? 0) > 0
          // term apr is already in percent ⇒ stableBorrowRate (percent) = the best of them
          const fixedBorrowRate = hasFixedTerms
            ? Math.min(...fixedTermsForMarket!.map((t) => t.apr))
            : 0

          const loanMarketUid = createMarketUid(chainId, m, loanTokenAddress)
          data[m].data[loanMarketUid] = {
            marketUid: loanMarketUid,
            name: 'Loan ' + (meta?.symbol ?? ''),
            poolId: loanTokenAddress,
            underlying: loanTokenAddress,
            asset: meta,
            totalDeposits,
            totalDebtStable: 0,
            totalDebt,
            totalDepositsUSD: totalDeposits * loanPrice,
            totalDebtStableUSD: 0,
            totalLiquidity: totalDeposits - totalDebt,
            borrowLiquidity: totalDeposits - totalDebt,
            totalLiquidityUSD: (totalDeposits - totalDebt) * loanPrice,
            borrowLiquidityUSD: (totalDeposits - totalDebt) * loanPrice,
            totalDebtUSD: totalDebt * loanPrice,
            // utilization is a WAD bigint from MathLib; surface as 0..1 number
            utilization: Number(utilization) / 1e18,
            depositRate: depositApr,
            variableBorrowRate: borrowApr,
            // best available fixed-term borrow rate (percent); 0 when not a brokered market
            stableBorrowRate: fixedBorrowRate,
            intrinsicYield:
              additionalYields?.intrinsicYields?.[meta?.assetGroup] ?? 0,
            rewards: loanRewards.length > 0 ? loanRewards : undefined,
            decimals: loanDecimals,
            config: {
              0: {
                category: 0,
                borrowCollateralFactor: 0,
                collateralFactor: 0,
                borrowFactor: 1,
                liquidationPenalty,
                closeFactor: 1,
                collateralDisabled: true,
                debtDisabled: false,
              },
            },
            closeFactor: 1,
            collateralActive: false,
            borrowingEnabled: true,
            depositsEnabled: true,
            // a fixed-term (stable) borrow rate is available on brokered markets
            hasStable: hasFixedTerms,
            // brokered markets are fixed-term only on the borrow side: there is no composable
            // variable borrow (the variableBorrowRate is informational — the dynamic/matured-loan
            // rate). Supply yield (depositRate) is still variable. Consumers must borrow via the
            // broker (termId / stableBorrowRate / fixedTerms), not as a regular variable market.
            variableBorrowDisabled: hasFixedTerms,
            isActive: true,
            isFrozen: false,
          }

          const metaCollateral =
            tokens[collateralAssetAddress] ?? collateralAsset
          // 2. COLLATERAL asset entry
          const collateralMarketUid = createMarketUid(
            chainId,
            m,
            collateralAssetAddress,
          )
          data[m].data[collateralMarketUid] = {
            marketUid: collateralMarketUid,
            name: 'Collateral ' + (metaCollateral?.symbol ?? ''),
            poolId: collateralAssetAddress,
            underlying: collateralAssetAddress,
            asset: metaCollateral,
            totalDeposits: 0, // we do not have these metrics when querying on-chain
            totalDebtStable: 0,
            totalDebt: 0,
            totalLiquidity: 0,
            borrowLiquidity: 0,
            totalLiquidityUSD: 0,
            borrowLiquidityUSD: 0,
            totalDepositsUSD: 0,
            totalDebtStableUSD: 0,
            totalDebtUSD: 0,
            utilization: 0,
            depositRate: 0,
            variableBorrowRate: 0,
            stableBorrowRate: 0,
            intrinsicYield:
              additionalYields?.intrinsicYields?.[metaCollateral?.assetGroup] ??
              0,
            rewards: undefined,
            decimals: collateralDecimals,
            config: {
              0: {
                category: 0,
                borrowCollateralFactor: ltv,
                collateralFactor: ltv,
                borrowFactor: 1,
                liquidationPenalty,
                closeFactor: 1,
                collateralDisabled: false,
                debtDisabled: true,
              },
            },
            closeFactor: 1,
            collateralActive: true,
            borrowingEnabled: false,
            depositsEnabled: true,
            hasStable: false,
            isActive: true,
            isFrozen: false,
          }

          data[m].params = {
            market: {
              lender: m,
              collateralDecimals: collateralDecimals,
              loanDecimals: loanDecimals,
              id: uniqueKey,
              lltv: lltv.toString(),
              oracle: oracle,
              irm: irm,
              collateralAddress: collateralAssetAddress,
              loanAddress: loanTokenAddress,
              minLoan: minLoan?.toString() ?? '0',
              broker,
              loanProvider,
              collateralProvider,
              hasWhitelist,
              fee: state.fee.toString(),
              rateAtTarget: state.rateAtTarget.toString(),
              rateCap: rateCap?.toString() ?? '0',
              rateFloor: rateFloor?.toString() ?? '0',
              // term menu for brokered markets (warm via fetchListaFixedTerms)
              terms: fixedTermsForMarket,
              // Canonical cross-protocol fixed-term descriptor (Lista + Midnight
              // share this shape). Lista: rolling-duration menu (no single
              // maturity), no market-level fees, and a per-loan early-repay
              // penalty (the concrete amount is position-level, on the loan's
              // `term.earlyRepayPenalty`).
              fixedTerm: {
                model: 'lista',
                maturity: undefined,
                fees: {},
                earlyRepay: { kind: 'penalty' },
                // A Lista fixed term is fronted by a single LendingBroker proxy —
                // the concrete counterparty offering the rate. Expose it as the
                // term provider (omit when this isn't a brokered market).
                provider:
                  broker && broker !== zeroAddress
                    ? { kind: 'broker', address: broker }
                    : undefined,
              } as FixedTermInfo,
            },
          }
          data[m].chainId = chainId
        }
      })

      return data
    },
    markets.length > 0
      ? Math.ceil(markets.length / LISTA_MARKET_CHUNK_SIZE)
      : 0,
  ]
}
